import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { 
  Gift, 
  Trophy,
  Loader2,
  Crown,
  Sparkles,
  Users,
  ChevronLeft,
  ChevronRight,
  Heart,
  Maximize,
  Settings,
  X,
  Timer,
  Building2,
  PartyPopper
} from "lucide-react";
import { publicAPI } from "../lib/api";
import { getImageUrl } from "../lib/utils";

// Helper to extract YouTube video ID
const getYouTubeId = (url) => {
  if (!url) return "";
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : "";
};

// Available aspect ratios
const ASPECT_RATIOS = {
  "16:9": { width: 16, height: 9, label: "16:9 (HD)" },
  "3:2": { width: 3, height: 2, label: "3:2 (Écran 3x2m)" },
  "16:10": { width: 16, height: 10, label: "16:10 (MacBook)" },
  "4:3": { width: 4, height: 3, label: "4:3 (Standard)" },
  "21:9": { width: 21, height: 9, label: "21:9 (Ultra-wide)" },
};

// Animated words for TIREO logo
const WORDS = ["hasard", "sort", "clic", "pif"];

// Animated TIREO component
const AnimatedTireoFooter = () => {
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex(prev => (prev + 1) % WORDS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center justify-center gap-3">
      <span className="text-xl text-white/80 font-medium">Propulsé par</span>
      <span className="font-black text-3xl text-blue-500">TIREO</span>
      <span className="relative w-20 h-8 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.span
            key={wordIndex}
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -30, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute left-0 text-white text-xl font-medium"
          >
            {WORDS[wordIndex]}
          </motion.span>
        </AnimatePresence>
      </span>
    </div>
  );
};

export default function PublicDrawShowResults() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [draw, setDraw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentWinnerIndex, setCurrentWinnerIndex] = useState(0);
  const [currentPartnerSlide, setCurrentPartnerSlide] = useState(0);
  const [allWinners, setAllWinners] = useState([]); // Flat list of all winners with their prizes
  const [customFieldLabel, setCustomFieldLabel] = useState(""); // Label for custom field
  const winnerInterval = useRef(null);
  const partnerInterval = useRef(null);
  const containerRef = useRef(null);
  
  // Settings
  const [showSettings, setShowSettings] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(searchParams.get("ratio") || "16:9");
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Handle ratio change
  const handleRatioChange = (newRatio) => {
    setAspectRatio(newRatio);
    setSearchParams({ ratio: newRatio });
  };

  // Fullscreen handling
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Fire confetti
  const fireConfetti = useCallback(() => {
    confetti({
      particleCount: 150,
      spread: 100,
      origin: { y: 0.6 },
      zIndex: 9999,
      colors: ['#FFD700', '#FFA500', '#FF6347', '#FF1493', '#00CED1']
    });
  }, []);

  // Load draw data
  useEffect(() => {
    loadDraw();
    const interval = setInterval(loadDraw, 10000);
    return () => {
      clearInterval(interval);
      if (winnerInterval.current) clearInterval(winnerInterval.current);
      if (partnerInterval.current) clearInterval(partnerInterval.current);
    };
  }, [id]);

  const loadDraw = async () => {
    try {
      const res = await publicAPI.getDraw(id);
      setDraw(res.data);
      
      // Create flat list of winners (one entry per winner, not per prize)
      if (res.data.status === "completed" && res.data.results?.length > 0) {
        const winners = res.data.results.map((result, index) => {
          const prize = res.data.prizes?.find(p => p.id === result.prize_id) || {};
          const participant = result.participant || {};
          return {
            rank: index + 1,
            winnerName: participant.display_name || participant.pseudo || result.participant_name || participant.name || "Gagnant",
            winnerPhone: participant.phone || "",
            winnerEmail: participant.email || "",
            winnerRef: (participant.reference || result.participant_id || "").substring(0, 8).toUpperCase(),
            winnerCustomField: participant.custom_field_value || result.participant_custom_field || "",
            prizeName: result.prize_name || prize.name || "Lot",
            prizeImage: prize.image_url || result.prize?.image_url,
            prizeDescription: prize.description || "",
            partnerName: prize.partner_name || "",
            partnerLogo: prize.partner_logo_url || ""
          };
        });
        setAllWinners(winners);
        // Also store the custom field label
        setCustomFieldLabel(res.data.custom_field_label || "");
      }
    } catch (error) {
      console.error("Error loading draw:", error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-slide winners with confetti
  useEffect(() => {
    if (allWinners.length > 0) {
      // Fire confetti on each slide change
      fireConfetti();
      
      if (allWinners.length > 1) {
        winnerInterval.current = setInterval(() => {
          setCurrentWinnerIndex(prev => {
            const next = (prev + 1) % allWinners.length;
            return next;
          });
        }, 5000);
      }
    }
    return () => {
      if (winnerInterval.current) clearInterval(winnerInterval.current);
    };
  }, [allWinners.length, fireConfetti]);

  // Fire confetti when winner changes
  useEffect(() => {
    if (allWinners.length > 0) {
      fireConfetti();
    }
  }, [currentWinnerIndex, allWinners.length, fireConfetti]);

  // Auto-slide partners
  useEffect(() => {
    const partners = draw?.partner_logos || [];
    if (partners.length > 1) {
      partnerInterval.current = setInterval(() => {
        setCurrentPartnerSlide(prev => (prev + 1) % partners.length);
      }, 4000);
    }
    return () => {
      if (partnerInterval.current) clearInterval(partnerInterval.current);
    };
  }, [draw?.partner_logos?.length]);

  // Get background style
  const getBackgroundStyle = () => {
    if (draw?.background_type === "image" && draw?.background_image) {
      return {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${getImageUrl(draw.background_image)})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      };
    }
    if (draw?.background_type === "color" && draw?.background_color) {
      return {
        background: `linear-gradient(135deg, ${draw.background_color} 0%, #0f172a 100%)`
      };
    }
    return { background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)' };
  };

  const hasVideoBackground = draw?.background_type === "video" && draw?.background_video_url;
  const videoId = hasVideoBackground ? getYouTubeId(draw.background_video_url) : null;

  // Calculate container dimensions based on aspect ratio
  const getContainerStyle = () => {
    const ratio = ASPECT_RATIOS[aspectRatio];
    if (!ratio) return { width: '100vw', height: '100vh' };
    
    if (aspectRatio === "16:9") {
      return { width: '100vw', height: '100vh' };
    }
    
    return {
      aspectRatio: `${ratio.width} / ${ratio.height}`,
      maxWidth: '100vw',
      maxHeight: '100vh',
      margin: 'auto'
    };
  };

  const partners = draw?.partner_logos || [];
  const currentPartner = partners[currentPartnerSlide];
  const currentWinner = allWinners[currentWinnerIndex];

  if (loading) {
    return (
      <div className="h-screen w-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2 }}>
          <Loader2 className="w-16 h-16 text-yellow-400" />
        </motion.div>
      </div>
    );
  }

  if (!draw) {
    return (
      <div className="h-screen w-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <p className="text-white text-2xl">Tirage non trouvé</p>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="h-screen w-screen overflow-hidden relative flex items-center justify-center"
      style={{ background: '#000' }}
    >
      {/* Ratio Container */}
      <div 
        className="relative overflow-hidden"
        style={{
          ...getContainerStyle(),
          ...(hasVideoBackground ? {} : getBackgroundStyle())
        }}
      >
        {/* YouTube Video Background */}
        {hasVideoBackground && videoId && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute inset-0 bg-black/70 z-10" />
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
              className="absolute top-1/2 left-1/2 w-[200vw] h-[200vh] -translate-x-1/2 -translate-y-1/2"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </div>
        )}

        {/* Settings Button */}
        <div className="absolute top-4 right-4 z-50 flex gap-2">
          <button
            onClick={toggleFullscreen}
            className="bg-black/50 hover:bg-black/70 text-white p-3 rounded-xl backdrop-blur transition-all"
            title="Plein écran"
          >
            <Maximize className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="bg-black/50 hover:bg-black/70 text-white p-3 rounded-xl backdrop-blur transition-all"
            title="Paramètres"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>

        {/* Settings Panel */}
        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              className="absolute top-16 right-4 z-50 bg-black/90 backdrop-blur-xl rounded-2xl p-6 w-72 border border-white/20"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold">Mode Projection</h3>
                <button onClick={() => setShowSettings(false)} className="text-white/60 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-white/70 text-sm block mb-2">Ratio d'écran</label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(ASPECT_RATIOS).map(([key, { label }]) => (
                      <button
                        key={key}
                        onClick={() => handleRatioChange(key)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          aspectRatio === key
                            ? "bg-yellow-500 text-black"
                            : "bg-white/10 text-white hover:bg-white/20"
                        }`}
                      >
                        {key}
                      </button>
                    ))}
                  </div>
                  <p className="text-white/40 text-xs mt-2">
                    {ASPECT_RATIOS[aspectRatio]?.label || aspectRatio}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ============ MAIN 3-COLUMN LAYOUT ============ */}
        <div className="h-full w-full flex relative z-20 p-4 gap-4">
          
          {/* ============ LEFT COLUMN - Stats & Partners ============ */}
          <div className="w-1/4 flex flex-col gap-3">
            {/* Completed Badge */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 backdrop-blur-xl rounded-2xl p-5 border border-green-500/30"
            >
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Trophy className="w-8 h-8 text-green-400" />
                  <span className="text-green-400 font-bold text-xl uppercase tracking-wider">
                    TERMINÉ
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-black/30 rounded-xl p-3">
                    <p className="text-3xl font-black text-white">{draw.participants?.length || 0}</p>
                    <p className="text-white/60 text-xs">PARTICIPANTS</p>
                  </div>
                  <div className="bg-black/30 rounded-xl p-3">
                    <p className="text-3xl font-black text-yellow-400">{allWinners.length}</p>
                    <p className="text-white/60 text-xs">GAGNANTS</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Current Winner Progress */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-yellow-500/20 to-amber-500/10 backdrop-blur-xl rounded-2xl p-4 border border-yellow-500/30"
            >
              <div className="flex items-center justify-center gap-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                  <Crown className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="text-4xl font-black text-white">{currentWinnerIndex + 1}/{allWinners.length}</p>
                  <p className="text-yellow-400/80 text-sm uppercase">Gagnant affiché</p>
                </div>
              </div>
            </motion.div>

            {/* Partners Carousel */}
            {draw.partner_carousel_enabled && partners.length > 0 && (
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 flex-1 flex flex-col">
                <p className="text-white text-center text-xl font-bold mb-4">
                  {draw.partner_carousel_title || "Merci à nos partenaires"}
                </p>
                
                <div className="flex-1 flex items-center justify-center min-h-[180px]">
                  <div className="bg-white rounded-3xl p-8 shadow-2xl">
                    {currentPartner?.url && (
                      <img
                        key={currentPartnerSlide}
                        src={getImageUrl(currentPartner.url)}
                        alt=""
                        className="h-32 w-auto max-w-[200px] object-contain"
                      />
                    )}
                  </div>
                </div>

                {partners.length > 1 && (
                  <div className="flex justify-center gap-2 mt-4">
                    {partners.map((_, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => setCurrentPartnerSlide(idx)}
                        className={`h-3 rounded-full transition-all ${idx === currentPartnerSlide ? "bg-white w-8" : "bg-white/30 w-3"}`} 
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ============ CENTER COLUMN - Title + Winner Showcase ============ */}
          <div className="w-1/2 flex flex-col">
            {/* TITLE with Results badge */}
            <motion.div 
              initial={{ opacity: 0, y: -20 }} 
              animate={{ opacity: 1, y: 0 }} 
              className="text-center mt-8 mb-4"
            >
              <div className="inline-flex items-center gap-3 mb-4">
                <PartyPopper className="w-8 h-8 text-yellow-400" />
                <span className="text-yellow-400 font-bold text-xl uppercase tracking-wider">Résultats</span>
                <PartyPopper className="w-8 h-8 text-yellow-400" style={{ transform: 'scaleX(-1)' }} />
              </div>
              <h1 
                className="text-5xl lg:text-6xl font-black text-white"
                style={{ fontFamily: 'Outfit', textShadow: '0 0 40px rgba(234, 179, 8, 0.4)' }}
              >
                {draw.title}
              </h1>
            </motion.div>

            {/* Winner Showcase - One winner at a time with prize */}
            {allWinners.length > 0 && (
              <>
                <div className="flex-1 flex items-center justify-center relative">
                  {/* Navigation arrows */}
                  {allWinners.length > 1 && (
                    <>
                      <button 
                        onClick={() => setCurrentWinnerIndex(prev => (prev - 1 + allWinners.length) % allWinners.length)} 
                        className="absolute left-0 z-30 bg-white/20 hover:bg-white/40 backdrop-blur rounded-full p-3"
                      >
                        <ChevronLeft className="w-6 h-6 text-white" />
                      </button>
                      <button 
                        onClick={() => setCurrentWinnerIndex(prev => (prev + 1) % allWinners.length)} 
                        className="absolute right-0 z-30 bg-white/20 hover:bg-white/40 backdrop-blur rounded-full p-3"
                      >
                        <ChevronRight className="w-6 h-6 text-white" />
                      </button>
                    </>
                  )}

                  <AnimatePresence mode="wait">
                    {currentWinner && (
                      <motion.div
                        key={currentWinnerIndex}
                        initial={{ opacity: 0, scale: 0.8, y: 50 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: -50 }}
                        transition={{ duration: 0.5 }}
                        className="w-full max-w-3xl mx-6"
                      >
                        <div className="bg-gradient-to-br from-yellow-500/20 via-white/10 to-amber-500/20 backdrop-blur-xl rounded-3xl overflow-hidden border border-yellow-500/50">
                          <div className="flex flex-row h-[420px]">
                            {/* Prize Image */}
                            <div className="w-1/2 h-full relative">
                              {currentWinner.prizeImage ? (
                                <img 
                                  src={getImageUrl(currentWinner.prizeImage)} 
                                  alt={currentWinner.prizeName} 
                                  className="w-full h-full object-cover" 
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-yellow-500/40 to-amber-600/40 flex items-center justify-center">
                                  <Gift className="w-32 h-32 text-yellow-400/60" />
                                </div>
                              )}
                              {/* Rank badge */}
                              <div className="absolute top-4 left-4 bg-gradient-to-br from-yellow-400 to-amber-500 text-black font-black px-4 py-2 rounded-full text-xl shadow-lg">
                                #{currentWinner.rank}
                              </div>
                              {/* Partner logo */}
                              {currentWinner.partnerLogo && (
                                <div className="absolute bottom-4 right-4 bg-white rounded-xl p-2">
                                  <img src={getImageUrl(currentWinner.partnerLogo)} alt="" className="h-10 w-auto object-contain" />
                                </div>
                              )}
                            </div>

                            {/* Prize Info + Winner Name */}
                            <div className="w-1/2 p-8 flex flex-col justify-center">
                              {/* Prize name */}
                              <div className="flex items-center gap-2 mb-4">
                                <Gift className="w-6 h-6 text-yellow-400" />
                                <span className="text-yellow-400/80 font-bold text-lg uppercase tracking-wider">
                                  {currentWinner.prizeName}
                                </span>
                              </div>

                              {/* WINNER NAME - Big and prominent */}
                              <div className="bg-gradient-to-r from-yellow-500/30 to-amber-500/30 rounded-2xl p-6 mb-4">
                                <div className="flex items-center gap-4 mb-2">
                                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-xl">
                                    <Crown className="w-10 h-10 text-white" />
                                  </div>
                                  <div>
                                    <p className="text-white/60 text-sm uppercase tracking-wider">Gagnant</p>
                                    <motion.h2 
                                      className="text-3xl lg:text-4xl font-black text-white"
                                      style={{ fontFamily: 'Outfit' }}
                                      initial={{ scale: 0.8 }}
                                      animate={{ scale: 1 }}
                                      transition={{ delay: 0.2, type: "spring" }}
                                    >
                                      {currentWinner.winnerName}
                                    </motion.h2>
                                    {/* Custom field display */}
                                    {customFieldLabel && currentWinner.winnerCustomField && (
                                      <p className="text-yellow-400 text-lg font-semibold mt-1">
                                        {customFieldLabel}: {currentWinner.winnerCustomField}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                  <Sparkles className="w-5 h-5 text-yellow-400" />
                                  <span className="text-yellow-400/80 text-sm">Réf: #{currentWinner.winnerRef}</span>
                                </div>
                              </div>

                              {/* Partner name */}
                              {currentWinner.partnerName && (
                                <div className="flex items-center gap-2 p-3 bg-white/10 rounded-xl mt-auto">
                                  <span className="text-white/50 text-sm">Offert par</span>
                                  <span className="text-white font-bold">{currentWinner.partnerName}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Winner dots */}
                {allWinners.length > 1 && (
                  <div className="flex justify-center gap-2 mt-3 mb-4">
                    {allWinners.map((_, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => setCurrentWinnerIndex(idx)} 
                        className={`h-2 rounded-full transition-all ${idx === currentWinnerIndex ? "bg-yellow-400 w-8" : "bg-white/30 w-2"}`} 
                      />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* TIREO Footer */}
            <div className="flex flex-col items-center justify-center mt-auto mb-2">
              <div className="w-48 h-px bg-white/30 mb-3" />
              <AnimatedTireoFooter />
            </div>
          </div>

          {/* ============ RIGHT COLUMN - Organizer & Beneficiary ============ */}
          <div className="w-1/4 flex flex-col gap-3">
            {/* Organizer */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/20 flex-[2] flex flex-col items-center justify-center text-center"
            >
              {draw.organizer_logo ? (
                <div className="bg-white rounded-2xl p-4 shadow-xl mb-3">
                  <img 
                    src={getImageUrl(draw.organizer_logo)} 
                    alt={draw.organizer_name}
                    className="h-20 w-auto object-contain"
                  />
                </div>
              ) : (
                <div className="h-24 w-24 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-xl mb-3">
                  <Building2 className="w-12 h-12 text-white" />
                </div>
              )}
              
              <p className="text-white/70 text-lg font-semibold uppercase tracking-wider mb-2">Organisé par</p>
              <h2 className="text-white text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                {draw.organizer_name}
              </h2>
            </motion.div>

            {/* Beneficiary */}
            {draw.beneficiary_name && (
              <motion.div 
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-pink-500/20 to-rose-500/10 backdrop-blur-xl rounded-2xl p-5 border border-pink-500/30 flex-[3] flex flex-col items-center justify-center text-center"
              >
                <p className="text-pink-300 text-lg font-bold uppercase tracking-wider mb-4 flex items-center justify-center gap-2">
                  <Heart className="w-6 h-6" />
                  AU PROFIT DE
                </p>
                
                {draw.beneficiary_logo ? (
                  <div className="bg-white rounded-2xl p-5 mx-auto mb-4 shadow-xl">
                    <img 
                      src={getImageUrl(draw.beneficiary_logo)} 
                      alt={draw.beneficiary_name}
                      className="h-24 w-auto object-contain"
                    />
                  </div>
                ) : (
                  <div className="h-24 w-24 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                    <Heart className="w-12 h-12 text-white" />
                  </div>
                )}
                
                <p className="text-pink-300 text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                  {draw.beneficiary_name}
                </p>
              </motion.div>
            )}

            {!draw.beneficiary_name && (
              <motion.div 
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 flex-[3] flex flex-col items-center justify-center"
              >
                <Trophy className="w-20 h-20 text-yellow-400 mb-4" />
                <p className="text-white/70 text-lg text-center">
                  Félicitations à tous les gagnants !
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
